import { FetchBaseQueryErrorType, OrderResponse } from '@afterpaytouch/portal-api'
import type { Money } from '@afterpay/types'
import { isValid, parseISO } from 'date-fns'
import {
  SUP_KEYS,
  isConflictError,
  isCreatingTooEarly,
  isCreatingTooLate,
  isDuplicatedEntityError,
  isExternalDisputeExistError,
  isInvalidAmountError,
  isTimesLimitError,
} from '../../model'

export const regularOrderLowerBound = 604800000 // 7 * 24 * 3600 * 1000
export const regularOrderUpperBound = 10368000000 // 120 * 24 * 3600 * 1000
export const supOrderLowerBound = 0
export const supOrderUpperBound = 2592000000 // 30 * 24 * 3600 * 1000

export const isSupOrder = (order: OrderResponse | null): boolean => SUP_KEYS.includes(order?.source ?? '')
export const checkCreateDisputeLowerBound = (order: OrderResponse): boolean => {
  const times = Date.now() - order.orderDate
  return times >= (isSupOrder(order) ? supOrderLowerBound : regularOrderLowerBound)
}

export const checkCreateDisputeUpperBound = (order: OrderResponse): boolean => {
  const times = Date.now() - order.orderDate
  return times <= (isSupOrder(order) ? supOrderUpperBound : regularOrderUpperBound)
}

export const checkCreateDisputeRefundableAmount = (order: OrderResponse): boolean => {
  return Number(order?.orderSummary?.totalAfterRefund?.amount) > 0
}

export const checkAmount = (amount: number): boolean => {
  return /^\d+(\.\d{1,2})?$/.test(String(amount))
}

export const convertSection75ItemToConsumerCurrency = (itemAmount: Money, consumerOrderTotal: Money, exchangeRate: number | null): Money | null => {
  const numericAmount = Number(itemAmount.amount)
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return null
  }

  if (itemAmount.currency === consumerOrderTotal.currency) {
    return {
      ...itemAmount,
      symbol: consumerOrderTotal.symbol ?? itemAmount.symbol,
    }
  }

  const numericExchangeRate = Number(exchangeRate)
  if (!Number.isFinite(numericExchangeRate) || numericExchangeRate <= 0) {
    return null
  }

  return {
    amount: (numericAmount / numericExchangeRate).toFixed(2),
    currency: consumerOrderTotal.currency,
    symbol: consumerOrderTotal.symbol,
  }
}

/**
 * Simple date validation that expects ISO date strings
 * @param date - The ISO date string to validate (YYYY-MM-DD)
 * @param order - The order response (for order date comparison)
 * @returns true if the date is invalid, false otherwise
 */
export const isDateInvalid = (date: string | null, order: OrderResponse): boolean => {
  if (!date) {
    return true
  }

  // Parse ISO date string
  const disputeDate = parseISO(date.trim())

  if (!isValid(disputeDate)) {
    return true // Invalid date format
  }

  // Set to end of the dispute date for comparison
  disputeDate.setHours(23, 59, 59, 999)
  const disputeTimestamp = disputeDate.getTime()

  const today = new Date()
  today.setHours(23, 59, 59, 999) // Set to end of today

  // Check if date is after today
  if (disputeTimestamp > today.getTime()) {
    return true
  }

  // Check if date is before order date
  if (Boolean(order?.orderDate) && disputeTimestamp < order.orderDate) {
    return true
  }

  return false
}

export const getDisputeErrorMessage = (error: unknown, t: (key: string, options?: any) => string): string => {
  if (isInvalidAmountError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result:failure:invalidAmount')
  }

  if (isConflictError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result:failure:conflict')
  }

  if (isTimesLimitError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result:failure:timesLimit.message')
  }

  if (isExternalDisputeExistError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result.failure.externalDisputeExist')
  }

  if (isDuplicatedEntityError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result.failure.duplicateEntity')
  }

  if (isCreatingTooEarly((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result.failure.tooEarly')
  }

  if (isCreatingTooLate((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result.failure.tooLate')
  }

  return t('result:failure:unspecific')
}

export const getDisputeErrorTitle = (error: unknown, t: (key: string, options?: any) => string): string => {
  if (isTimesLimitError((error as FetchBaseQueryErrorType)?.data?.errorCode)) {
    return t('result:failure:timesLimit.title')
  }
  return t('result:failure:head')
}

/**
 * Check if an amount meets the Section 75 minimum threshold (over £100)
 * @param amount - The amount as a string or number
 * @returns boolean - True if the amount is over 100
 */
export const isAmountOverSection75Threshold = (amount: string | number): boolean => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(numericAmount) || numericAmount <= 0) {
    return false
  }

  return numericAmount > 100
}

/**
 * Normalise an order item's quantity to a positive integer, defaulting to a single unit.
 * Older orders and manually entered items carry no quantity at all.
 */
export const section75ItemQuantity = (quantity?: number | null): number => {
  const numericQuantity = Number(quantity)
  return Number.isFinite(numericQuantity) && numericQuantity > 0 ? Math.floor(numericQuantity) : 1
}

/**
 * The claim for a whole line: per-unit price x quantity. Disputing 2 x GBP 119 means
 * claiming GBP 238, not GBP 119.
 */
export const section75LineTotal = (unitAmount?: string | number | null, quantity?: number | null): string => {
  const numericUnitAmount = Number(unitAmount)
  const safeUnitAmount = Number.isFinite(numericUnitAmount) && numericUnitAmount > 0 ? numericUnitAmount : 0
  return (safeUnitAmount * section75ItemQuantity(quantity)).toFixed(2)
}
